import { Fragment } from 'react';
import classNames from 'classnames';

import RightSmallLine from '@/assets/icons/right_small_line.svg?component';
import {
    type AuditFilterChange,
    type AuditScopeKind,
    type AuditScopeSegment,
    addAuditFilters,
    scopeSegmentText,
    useAuditText,
} from '@/domain/auditLog';

import CellFilterButton from './CellFilterButton';
import CellIcon from './CellIcon';

import styles from './ScopePath.module.scss';

interface ScopePathProps {
    segments: readonly AuditScopeSegment[];
    onFilter: (change: AuditFilterChange) => void;
}

// The client gives way first, the site only once the client is down to its minimum, and
// the pen never, so a deleted pen's state stays readable when the path is too long.
const SHRINK_CLASS: Record<AuditScopeKind, string> = {
    client: styles.client,
    location: styles.location,
    cage: styles.cage,
};

/** "Client A › Site North › Pen 5", each level a click that shows only that scope. */
export default function ScopePath({ segments, onFilter }: ScopePathProps) {
    const render = useAuditText();
    return (
        <>
            {segments.map((segment, index) => {
                const { name, stateLabel, title } = segment;
                return (
                    <Fragment key={segment.kind}>
                        {index > 0 ? (
                            <CellIcon
                                icon={RightSmallLine}
                                size="small"
                                className={styles.separator}
                            />
                        ) : null}
                        <span className={classNames(styles.segment, SHRINK_CLASS[segment.kind])}>
                            <CellFilterButton
                                value={render(scopeSegmentText(segment))}
                                detail={title === null ? undefined : render(title)}
                                onFilter={() => onFilter(addAuditFilters(segment.filter))}>
                                {render(name)}
                                {stateLabel === null ? null : (
                                    <span className={styles.state}>{render(stateLabel)}</span>
                                )}
                            </CellFilterButton>
                        </span>
                    </Fragment>
                );
            })}
        </>
    );
}
